import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import DateFieldContainer from "../../../ui/layout/page/forms-container/form-item-container/date-field-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import NationalityFieldContainer from "../../../ui/layout/page/forms-container/form-item-container/nationality-field-container";
import RadioButtonContainer from "../../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import Info from "../../../ui/widgets/info";
import { mapLanguageToEvent } from "../../../util/tracking/eventActionMapper";
import GTM from "../../../util/tracking/gtm";
import { EventCategory } from "../../../util/tracking/types";
import BirthDate from "../../shared/components/birth-date";
import CompanyPosition from "../../shared/components/company-position";
import RadioButtonField from "../../shared/components/formik/radiobutton";
import Gender from "../../shared/components/gender";
import Language from "../../shared/components/language";
import Nationality from "../../shared/components/nationality";
import CardLimit from "./card-limit";
import { CardholderFormProps } from "./types";
import useAnnualGrossIncomeValues from "./useAnnualGrossIncomeValues";
import useResidencePermitValues from "./useResidencePermitValues";
import { Grid } from "@mui/material";
import { ProductContext } from "../../../context/product-context/productContext";
import { FullNameAccordion } from "./full-name-accordion";
import { AddressAccordion } from "./address-accordion";
import { ContactAccordion } from "./contact-accordion";
import { CardholderFormikContext } from "../context";
import FundsOwnership from "./funds-ownership";
import { IbanAutocomplete } from "../../../ui/widgets/iban";

export default function CardholderForm({
  nationality,
  birthdate,
  firstName,
  lastName,
  fullStreet,
  address,
  phoneBusiness,
  mobile,
  emailBusiness,
  companyPosition,
  spendingLimit,
  spendingLimitOther,
  hasDecentralizedInvoicing,
  isSameAsAdmin,
  isSoleProprietorship,
}: CardholderFormProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { selectedProduct } = useContext(ProductContext);
  const { values, setFieldValue } = useContext(CardholderFormikContext);

  const dataPath = isSameAsAdmin ? "adminAsCardholder." : "cardholder.";
  const annualGrossIncomeValues = useAnnualGrossIncomeValues(dataPath);
  const residencePermitValues = useResidencePermitValues(dataPath);

  const hasOtherNationality = nationality && nationality !== "CH";
  const livesInOtherCountry =
    address.country &&
    address.country.alpha2 &&
    address.country.alpha2 !== "CH";

  const hasDecentralizedInvoicingFeature = selectedProduct?.hasFeature(
    "DECENTRALIZED_INVOICING",
  );
  const hasDecentralizedInvoicingSelected =
    hasDecentralizedInvoicingFeature && hasDecentralizedInvoicing === true;

  return (
    <Grid className="scd-container-base" container key={dataPath}>
      <>
        <Gender dataPath={dataPath} />

        <Grid className="scd-container-base" container>
          <>
            <FullNameAccordion
              dataPath={dataPath}
              firstName={firstName}
              lastName={lastName}
            />

            <AddressAccordion
              dataPath={dataPath}
              address={address}
              fullStreet={fullStreet}
            />

            <ContactAccordion
              dataPath={dataPath}
              emailBusiness={emailBusiness}
              isSameAsAdmin={isSameAsAdmin}
              mobile={mobile}
              phoneBusiness={phoneBusiness}
            />
          </>
        </Grid>

        <DateFieldContainer>
          <BirthDate dataPath={dataPath} value={birthdate} />
        </DateFieldContainer>
        <NationalityFieldContainer>
          <Nationality
            dataPath={dataPath}
            value={nationality}
            onSelectCallback={(e) => {
              GTM.trackEvent(
                EventCategory.CardHolder,
                e.target.value as string,
              );
            }}
          />
        </NationalityFieldContainer>
        {hasOtherNationality && !livesInOtherCountry && (
          <RadioButtonContainer>
            <RadioButtonField
              title={getMessage("PermitTypeQuestion.description")}
              id={`${dataPath}residencePermit`}
              name={`${dataPath}residencePermit`}
              values={residencePermitValues}
            />
          </RadioButtonContainer>
        )}
        {hasOtherNationality && livesInOtherCountry && (
          <RadioButtonContainer>
            <RadioButtonField
              wrap={false}
              title={getMessage("IsCrossBorderWorkerQuestion.description")}
              id={`${dataPath}isCrossBorderWorker`}
              name={`${dataPath}isCrossBorderWorker`}
              values={[
                {
                  id: `${dataPath}isCrossBorderWorker-no`,
                  value: "no",
                  label: getMessage("YesNo.NO"),
                },
                {
                  id: `${dataPath}isCrossBorderWorker-yes`,
                  value: "yes",
                  label: getMessage("YesNo.YES"),
                },
              ]}
            />
          </RadioButtonContainer>
        )}
        <RadioButtonContainer>
          <Language
            dataPath={dataPath}
            onChange={(_, newValue) =>
              GTM.trackEvent(
                EventCategory.CorrespondenceLanguage,
                `KA - ${mapLanguageToEvent(newValue)}`,
              )
            }
          />
        </RadioButtonContainer>
        <InputContainer>
          <CompanyPosition dataPath={dataPath} value={companyPosition} />
        </InputContainer>
        <CardLimit
          dataPath={dataPath}
          spendingLimit={spendingLimit}
          spendingLimitOther={spendingLimitOther}
        />
        {!!isSoleProprietorship || (
          <RadioButtonContainer>
            <RadioButtonField
              wrap={false}
              title={getMessage("IsInPermanentPositionQuestion.description")}
              id={`${dataPath}isCurrentPermanentEmployee`}
              name={`${dataPath}isCurrentPermanentEmployee`}
              values={[
                {
                  id: `${dataPath}isCurrentPermanentEmployee-no`,
                  value: "no",
                  label: getMessage("YesNo.NO"),
                },
                {
                  id: `${dataPath}isCurrentPermanentEmployee-yes`,
                  value: "yes",
                  label: getMessage("YesNo.YES"),
                },
              ]}
            />
          </RadioButtonContainer>
        )}
        <RadioButtonContainer>
          <RadioButtonField
            title={getMessage("AnnualGrossIncome.description")}
            id={`${dataPath}annualGrossIncome`}
            name={`${dataPath}annualGrossIncome`}
            values={annualGrossIncomeValues}
          />
        </RadioButtonContainer>

        {!hasDecentralizedInvoicingFeature && (
          <Info title={getMessage("Cardholder.billingInfo-title")}>
            {getMessage("Cardholder.billingInfo-content")}
          </Info>
        )}

        {hasDecentralizedInvoicingSelected && (
          <IbanAutocomplete
            dataPath="decentralizedInvoicing.bankAccount."
            iban={values.decentralizedInvoicing.bankAccount.iban}
            label={getMessage("BankAccount.cardholder.iban")}
            callback={(code: string, name: string) => {
              setFieldValue(
                "decentralizedInvoicing.bankAccount.bankCode",
                code,
              );
              setFieldValue(
                "decentralizedInvoicing.bankAccount.bankName",
                name,
              );
            }}
          />
        )}

        {hasDecentralizedInvoicingSelected && (
          <FundsOwnership
            value={values.decentralizedInvoicing.fundsOwnership}
            cardholder={values.fundsOwnershipCardholder}
          />
        )}
      </>
    </Grid>
  );
}
